import { useRef, useState } from 'react';

import { toPivotFileViews } from '@/lib/price-list-break-pivot/fileView';
import {
  EMPTY_MAPPING,
  nextMapping,
} from '@/lib/price-list-break-pivot/guessMapping';
import {
  mergeParseResults,
  parsePriceListFile,
  uniqueHeaders,
} from '@/lib/price-list-break-pivot/parseWorkbook';
import { summarizePivotViews } from '@/lib/price-list-break-pivot/summarizePivot';
import type {
  GuessedMapping,
  ParseWorkbookResult,
} from '@/lib/price-list-break-pivot/types';
import {
  pivotedSheetsFromViews,
  writePivotedWorkbook,
} from '@/lib/price-list-break-pivot/writePivotedWorkbook';
import { triggerDownload } from 'src/lib/pricelist-utils';

const XLSX_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

interface PivotState {
  results: ParseWorkbookResult[];
  mapping: GuessedMapping;
}

const EMPTY_STATE: PivotState = {
  results: [],
  mapping: EMPTY_MAPPING,
};

function withResults(
  mapping: GuessedMapping,
  results: ParseWorkbookResult[],
): PivotState {
  if (results.length === 0) return EMPTY_STATE;
  return {
    results,
    mapping: nextMapping(mapping, uniqueHeaders(results)),
  };
}

export function downloadFileName(
  results: readonly ParseWorkbookResult[],
): string {
  const only = results[0];
  if (results.length === 1 && only) {
    return only.fileName.replace(/\.(xlsx|xls)$/i, '') + '-pivoted.xlsx';
  }
  return 'price-list-break-pivot.xlsx';
}

/**
 * Holds dropped price-list workbooks and the column mapping used to pivot
 * quantity-break rows. Each upload gets its own id so same-named files stay.
 */
export function usePriceListBreakPivot() {
  const [state, setState] = useState<PivotState>(EMPTY_STATE);
  const [isParsing, setIsParsing] = useState(false);
  const generationRef = useRef(0);
  const { results, mapping } = state;

  const headers = uniqueHeaders(results);
  const views = toPivotFileViews(results, mapping);
  const summary = summarizePivotViews(views);

  function invalidateInFlight(): void {
    generationRef.current += 1;
    setIsParsing(false);
  }

  async function addFiles(files: File[]): Promise<void> {
    const generation = generationRef.current + 1;
    generationRef.current = generation;
    setIsParsing(true);
    const incoming = await Promise.all(files.map(parsePriceListFile));
    if (generation !== generationRef.current) return;
    setState((current) =>
      withResults(
        current.mapping,
        mergeParseResults(current.results, incoming),
      ),
    );
    setIsParsing(false);
  }

  function removeFile(id: string): void {
    setState((current) =>
      withResults(
        current.mapping,
        current.results.filter((result) => result.id !== id),
      ),
    );
  }

  function clear(): void {
    invalidateInFlight();
    setState(EMPTY_STATE);
  }

  function setMapping(next: GuessedMapping): void {
    setState((current) => ({ ...current, mapping: next }));
  }

  function downloadPivot(): void {
    const sheets = pivotedSheetsFromViews(views);
    if (sheets.length === 0) return;
    const buffer = writePivotedWorkbook(sheets);
    triggerDownload(
      new Blob([buffer], { type: XLSX_TYPE }),
      downloadFileName(results),
    );
  }

  return {
    results,
    views,
    headers,
    mapping,
    setMapping,
    summary,
    isParsing,
    addFiles,
    removeFile,
    clear,
    downloadPivot,
  };
}
